import { motion } from "framer-motion";
import { 
  Zap, 
  TrendingUp, 
  HeadphonesIcon, 
  ShieldCheck, 
  Users 
} from "lucide-react";

const WhyChooseUs = () => {
  const reasons = [
    {
      icon: Zap,
      title: "Fast Delivery",
      description: "Quick turnaround without compromising quality"
    },
    {
      icon: TrendingUp,
      title: "Scalable Solutions",
      description: "Built to grow with your business needs"
    },
    {
      icon: HeadphonesIcon,
      title: "24/7 Support",
      description: "Round-the-clock assistance when you need it"
    },
    {
      icon: ShieldCheck,
      title: "Secure & Reliable",
      description: "Enterprise-grade security for your peace of mind"
    },
    {
      icon: Users,
      title: "Expert Developers",
      description: "Skilled team with diverse technical expertise"
    }
  ];

  return (
    <section 
      className="relative py-24 sm:py-32 overflow-hidden"
      data-testid="why-choose-us-section"
    >
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#0F172A]/50 via-transparent to-[#0F172A]/50" />

      <div className="relative max-w-7xl mx-auto px-6 sm:px-12 lg:px-24">
        {/* Section Header */}
        <motion.div
          className="text-center max-w-3xl mx-auto mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-50px" }}
          transition={{ duration: 0.6 }}
        >
          <span className="text-xs sm:text-sm font-bold tracking-[0.2em] uppercase text-blue-400 mb-4 block">
            Why Choose Us
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white mb-6">
            Your Success Is Our Priority
          </h2>
          <p className="text-lg text-slate-300">
            We combine technical excellence with dedicated service to deliver exceptional results.
          </p>
        </motion.div>

        {/* Reasons Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-6">
          {reasons.map((reason, index) => (
            <motion.div
              key={index}
              className="group relative"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
            >
              <div className="glass rounded-2xl p-6 h-full text-center glass-hover relative overflow-hidden">
                {/* Glow effect on hover */}
                <div className="absolute inset-0 bg-gradient-to-br from-blue-600/0 to-cyan-500/0 group-hover:from-blue-600/10 group-hover:to-cyan-500/10 transition-all duration-500" />
                
                {/* Icon */}
                <div className="relative w-14 h-14 mx-auto mb-4 rounded-xl bg-blue-600/20 flex items-center justify-center group-hover:bg-blue-600/30 transition-colors">
                  <reason.icon className="w-7 h-7 text-blue-400" />
                </div>

                {/* Content */}
                <h3 className="text-white font-semibold mb-2 relative">{reason.title}</h3>
                <p className="text-sm text-slate-400 relative">{reason.description}</p>

                {/* Border glow on hover */}
                <div className="absolute inset-0 rounded-2xl border border-transparent group-hover:border-blue-500/30 transition-colors" />
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default WhyChooseUs;
